import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

import { NubieAiQuestion } from '../../types/api';

/**
 * Les questions de NubieAI, rendues en PROPOSITIONS A CLIQUER.
 *
 * POURQUOI CE COMPOSANT EXISTE. L'entretien du premier tour sortait en prose : sept
 * questions numerotees que le client devait rediger. Signale par l'utilisateur -- « saisir
 * ces infos c'est fastidieux pour les clients, il faudrait des propositions avec bouton
 * pour valider ». Un commercant qui doit taper sept reponses abandonne, et une page sans
 * matiere est precisement le defaut que l'entretien devait corriger.
 *
 * CE QUE L'ECRAN DOIT DIRE, ET C'EST LA MOITIE QUI COMPTE : les propositions sont des
 * SUPPOSITIONS du modele, pas des faits. Rien de ce que le client ne valide pas n'atteint
 * sa page. Sans cette phrase a l'ecran, le bouton devient une machine a publier des faits
 * inventes sur un commerce reel -- un prix accepte sans etre lu, un volume approximatif
 * presente comme exact.
 *
 * TROIS CHOSES QUE CE COMPOSANT NE FAIT PAS, et chacune est un choix :
 *
 * 1. Il n'envoie JAMAIS une question sautee comme « non renseigne ». Une question sans
 *    reponse est simplement ABSENTE du message. Dire « prix : non renseigne » ferait
 *    ecrire « prix non communique » sur la page -- une mention que personne n'a demandee.
 * 2. Il ne pre-coche rien. Une case cochee d'avance est une reponse que le client n'a pas
 *    donnee, et le clic de validation ne la distingue plus de celles qu'il a choisies.
 * 3. Il ne bloque pas sur les questions obligatoires. Un commercant peut ne pas savoir, et
 *    le prompt sait deja ecrire avec une reponse partielle. Le bouton « fais au mieux »
 *    est la sortie explicite -- cette phrase est reconnue par le prompt, qui ecrit alors
 *    immediatement sans reposer de question.
 */

const traduire = useTranslation as unknown as (
  ns: string
) => { t: (cle: string, options?: Record<string, unknown>) => string };

interface Reponse {
  choix: string[];
  libre: string;
}

interface Props {
  questions: NubieAiQuestion[];
  envoi: boolean;
  onRepondre: (message: string) => void;
}

const QuestionsPanel: React.FC<Props> = ({ questions, envoi, onRepondre }) => {
  const { t } = traduire('nubieai');
  const [reponses, setReponses] = useState<Record<string, Reponse>>({});

  const lire = (id: string): Reponse => reponses[id] || { choix: [], libre: '' };

  const basculer = (q: NubieAiQuestion, prop: string) => {
    const r = lire(q.id);
    // `choix_multiple` accumule ; les deux autres genres remplacent -- et un second clic
    // sur la meme proposition la RETIRE, sans quoi un choix unique ne se corrige plus.
    const choix =
      q.genre === 'choix_multiple'
        ? r.choix.includes(prop)
          ? r.choix.filter((c) => c !== prop)
          : [...r.choix, prop]
        : r.choix.includes(prop)
        ? []
        : [prop];
    setReponses({ ...reponses, [q.id]: { ...r, choix } });
  };

  const ecrire = (id: string, libre: string) => {
    setReponses({ ...reponses, [id]: { ...lire(id), libre } });
  };

  /**
   * Le message envoye au modele, AUTO-DESCRIPTIF.
   *
   * Il reprend le TEXTE de chaque question, pas son `id` : le fil ne garde que les mots
   * et le modele ne relit pas sa propre question. « 150-300 F » seul ne dirait rien ;
   * « Prix d'une baguette ? 150-300 F » se comprend sans contexte. C'est ce qui permet au
   * fil de rester « les mots seulement » et au contexte d'etre regenere a chaque tour.
   */
  const message = useMemo(() => {
    const lignes: string[] = [];
    questions.forEach((q) => {
      const r = lire(q.id);
      const valeurs = [...r.choix];
      if (r.libre.trim()) valeurs.push(r.libre.trim());
      // ABSENTE si sans reponse -- voir le point 1 de l'en-tete.
      if (valeurs.length) lignes.push(`${q.texte} ${valeurs.join(', ')}`);
    });
    return lignes.join('\n');
  }, [questions, reponses]); // eslint-disable-line react-hooks/exhaustive-deps

  const aRepondu = message.length > 0;

  const classeChoix = (actif: boolean) =>
    [
      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button text-sm border transition-colors',
      actif
        ? 'bg-accent text-accent-contrast border-accent'
        : 'bg-surface-0 text-text-primary border-border-1 hover:border-border-2',
    ].join(' ');

  return (
    <div className="bg-surface-1 border border-border-1 rounded-card p-4">
      <h3 className="text-sm font-medium text-text-primary">{t('questions.titre')}</h3>
      {/* LA PHRASE QUI TIENT L'INVARIANT. Elle n'est pas decorative : sans elle, le
          client ne sait pas que ce qu'il lit sont des suppositions. */}
      <p className="mt-1 text-xs text-text-tertiary">{t('questions.aide')}</p>

      <div className="mt-4 flex flex-col gap-4">
        {questions.map((q) => {
          const r = lire(q.id);
          return (
            <div key={q.id}>
              <p className="text-sm text-text-primary">
                {q.texte}
                {q.facultatif && (
                  <span className="ml-1.5 text-xs text-text-quaternary">
                    {t('questions.facultatif')}
                  </span>
                )}
                {q.genre === 'choix_multiple' && (
                  <span className="ml-1.5 text-xs text-text-quaternary">
                    {t('questions.plusieurs')}
                  </span>
                )}
              </p>

              {q.propositions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.propositions.map((prop) => {
                    const actif = r.choix.includes(prop);
                    return (
                      <button
                        key={prop}
                        type="button"
                        onClick={() => basculer(q, prop)}
                        disabled={envoi}
                        className={classeChoix(actif)}
                      >
                        {actif && <CheckIcon className="w-3.5 h-3.5" strokeWidth={2.2} />}
                        {prop}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* L'ECHAPPATOIRE, ET ELLE EST NECESSAIRE : les propositions sont des
                  suppositions, donc le client doit pouvoir dire autre chose. Sans ce
                  champ, les boutons deviendraient coercitifs -- il cliquerait le moins
                  faux plutot que de dire le vrai. */}
              <input
                value={r.libre}
                onChange={(e) => ecrire(q.id, e.target.value)}
                disabled={envoi}
                placeholder={
                  q.propositions.length
                    ? (t('questions.autre') as string)
                    : (t('questions.votreReponse') as string)
                }
                className="mt-2 w-full px-3 py-2 bg-surface-0 border border-border-1 rounded-input text-sm text-text-primary placeholder-text-quaternary focus:outline-none focus:border-accent disabled:opacity-50"
              />
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => onRepondre(message)}
          disabled={envoi || !aRepondu}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-accent text-accent-contrast rounded-button text-sm font-semibold hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <PaperAirplaneIcon className="w-4 h-4" strokeWidth={1.8} />
          {envoi ? t('questions.envoi') : t('questions.valider')}
        </button>
        {/* « FAIS AU MIEUX » EST RECONNU PAR LE PROMPT, qui ecrit alors immediatement
            sans reposer de question. Ce n'est donc pas un abandon : c'est la sortie
            prevue pour qui ne veut pas repondre. */}
        <button
          type="button"
          onClick={() => onRepondre(t('questions.auMieuxMessage') as string)}
          disabled={envoi}
          className="px-4 py-2.5 text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-40 transition-colors"
        >
          {t('questions.auMieux')}
        </button>
      </div>
    </div>
  );
};

export default QuestionsPanel;
